export type ApiErrorType =
  | 'Generic'
  | 'NotFound'
  | 'AlreadyExists'
  | 'Unauthorized'
  | 'Forbid'
  | 'TooManyRequests';

export interface ApiErrorBody {
  type: ApiErrorType;
  message: string;
}

const DEFAULT_MESSAGE =
  "Si è verificato un errore inaspettato. Riprova più tardi o contatta l'assistenza tecnica.";

/** Il tipo di errore il BE lo dice con lo stato HTTP, non nel corpo. */
const TYPE_BY_STATUS: Record<number, ApiErrorType> = {
  400: 'Generic',
  401: 'Unauthorized',
  403: 'Forbid',
  404: 'NotFound',
  409: 'AlreadyExists',
  429: 'TooManyRequests',
};

/**
 * Il corpo di un errore previsto è il solo messaggio, in chiaro. È scritto in
 * inglese e per chi sviluppa: serve a capire cos'è successo, non a essere
 * mostrato — le frasi per l'operatore le mette il servizio che chiama, in base
 * al tipo.
 */
export function extractApiError(error: unknown): ApiErrorBody | null {
  const response = error as { status?: number; error?: unknown };
  const body = response?.error;

  // Corpo strutturato: resta buono se un endpoint torna a mandare tipo e messaggio.
  if (
    body &&
    typeof body === 'object' &&
    typeof (body as ApiErrorBody).type === 'string' &&
    typeof (body as ApiErrorBody).message === 'string'
  ) {
    return body as ApiErrorBody;
  }

  const type = TYPE_BY_STATUS[response?.status ?? 0];
  if (!type) return null;

  return { type, message: extractMessage(body) };
}

/**
 * Traduce l'errore in una frase da mostrare: `messages` copre i casi che il
 * servizio sa spiegare, il resto finisce sul messaggio generico.
 */
export function apiErrorMessage(
  error: unknown,
  messages: Partial<Record<ApiErrorType, string>>,
  fallback: string = DEFAULT_MESSAGE,
): string {
  const apiError = extractApiError(error);
  return (apiError && messages[apiError.type]) || fallback;
}

/** Testo semplice: non essendo JSON valido, Angular lo lascia in `text`. */
function extractMessage(body: unknown): string {
  if (typeof body === 'string') return body;

  if (
    body &&
    typeof body === 'object' &&
    typeof (body as { text?: unknown }).text === 'string'
  ) {
    return (body as { text: string }).text;
  }

  return '';
}
